import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { Observable, forkJoin, fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, share } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../../../components/itemsPerPage.component';
import { I18nTranslator } from '../../../../i18n/translate';
import { ARBleCampaign, ARBleOrderItem, ARBleParcel } from '../../../../models/agrorendement-ble.model';
import { Campaign } from '../../../../models/campaign.model';
import { OrderItemDeletionFailure, OrderItemDeletionSummary } from '../../../../models/order-item-deletion.model';
import { ListItem, Specy } from '../../../../models/lists.model';
import { Organisation } from '../../../../models/organisation.model';
import { Page } from '../../../../models/page.model';
import { CampaignYearPipe } from '../../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../../../pipes/hectareConverter.pipe';
import { ToLabelPipe } from '../../../../pipes/toLabel.pipe';
import { ARBleListType, ARBleListsService } from '../../../../services/agrorendement-ble-lists.service';
import { ARBleParcelFilter, ARBleService } from '../../../../services/agrorendement-ble.service';
import { ARCommonListType, ARCommonListsService } from '../../../../services/agrorendement-common-lists.service';
import { BeApiService } from '../../../../services/beapi.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { ListType, ListsService } from '../../../../services/lists.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { TOOLTIP_TEXTS } from '../../../../utils/tooltip-texts';
import {
  currentCampaign,
  extractErrorMessage,
  showErrorMessage,
  toBoolean,
  toNull,
  toNumber,
} from '../../../../utils/utils';
import { CustomStatusModalComponent, StatusModalContext } from '../../../planning/custom-status-modal.component';
import {
  ParcelDetailsDialogComponent,
  ParcelDetailsDialogContext,
} from '../../../parcel/parcel-details-dialog.component';
import { ARBleUtils } from './ble.utils';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'ar-Ble-parcels-cmp',
  templateUrl: 'parcels.component.html',
  styleUrls: ['parcels.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    ItemsPerPageComponent,
    FormsModule,
    Tooltip,
    TooltipContent,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    TranslatePipe,
    PaginationModule,
    TooltipModule,
  ],
  providers: [ARBleService, OrganisationService, BeApiService],
})
export class ARBleParcelComponent implements OnInit, AfterViewInit, OnDestroy {
  error: any;
  @ViewChild('termOrganisationFilterText') termOrganisationFilterText: ElementRef;
  @ViewChild('termPrefixFilterParcel') termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterExploitation') termPrefixFilterExploitation: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termCompletionStatusFilter') termCompletionStatusFilter: ElementRef;
  @ViewChild('termOrderStatusFilter') termOrderStatusFilter: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;
  @ViewChild('termSpecyFilter') termSpecyFilter: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;
  @ViewChildren('parcelCheckbox') parcelCheckboxes: any;
  @ViewChild('checkboxAll') checkboxAll: any;
  lengthAllParcels: number;
  checkedParcelIds = new Set<number>();
  openedCampaignsDateChecks = new Map<number, any>();
  productTypesIndex = new Map<number, string>();
  frequenciesIndex = new Map<number, string>();
  consoleModulationTypesIndex = new Map<number, string>();

  prefixFilterParcel: string = '';
  prefixFilterExploitation: string = '';
  prefixFilterIdParcel: number = null;
  campaignFilter: number = null;
  completionStatusFilter: boolean = null;
  orderStatusFilter: boolean = null;
  organisationFilter: number = null;
  organisationFilterText: string = '';
  showOrgaDropdown: boolean = false;
  filteredOrganisations: Organisation[] = [];
  specyFilter: number | string = null;
  advisorFilter: number | string = null;

  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;

  parcels: ARBleParcel[] = [];
  page: number = 1;
  itemsPerPage: number = 25;
  maxSize: number = 5;
  numPages: number = 1;
  length: number = 0;
  speciesDict: Map<number, Specy>;
  species: Specy[];

  campaigns: ARBleCampaign[];
  numAvailableParcels = 0;

  isAdmin: boolean;
  isNegoce: boolean;
  isManager: boolean;
  canRemoveOrders: boolean;
  showBeApi = false;

  showOrganisationFilter = false;
  organisations: Organisation[];

  tooltipTexts = TOOLTIP_TEXTS;

  // static pour garder entre affichages de la liste
  static filterToApply: ARBleParcelFilter;
  static allFiltersToShow: boolean;

  private destroy$ = new Subject<any>();

  constructor(
    private bleService: ARBleService,
    private bleListsService: ARBleListsService,
    private commonListsService: ARCommonListsService,
    private beApiService: BeApiService,
    private organisationService: OrganisationService,
    private listsService: ListsService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private route: ActivatedRoute,
    private modal: ModalService,
    private cdr: ChangeDetectorRef,
  ) {
    const authz = KeycloakService.auth.authz;
    this.isAdmin = authz.hasRealmRole('ROLE_ADMIN');
    this.isNegoce = authz.hasRealmRole('ROLE_NEGOCE');
    this.isManager = authz.hasRealmRole('ROLE_MANAGER');
    this.canRemoveOrders = !authz.hasRealmRole('ROLE_FARMER') && !authz.hasRealmRole('ROLE_ADVISOR');
  }

  getParcelsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: ARBleParcelFilter) {
    this.bleService
      .getPageParcels(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe((page) => this.handlePage(page));
  }

  getNumAvailableParcels() {
    this.bleService.countOrderItems(null, null, true).subscribe(
      (count) => {
        this.numAvailableParcels = count;
      },
      (error) => {
        this.handleError(error, this.i18nTranslator.translate('ar.table.error.recupParcellesDispo'));
      },
    );
  }

  handlePage(page: Page<ARBleParcel>) {
    this.parcels = page.content || [];
    this.numPages = page.totalPages || 1;
    this.length = page.totalElements || 0;
    this.beApiService.fillBeApiStatus(this.parcels, this.showBeApi);
  }

  ngOnInit() {
    let routeStatus: string = null;
    let onlyIncomplete = false;
    let routeCampaignId: number = null;

    this.route.params.subscribe((params) => {
      routeStatus = params['status'];
      onlyIncomplete = params['onlyIncomplete'] === '1' || params['onlyIncomplete'] === true;
      if (params['campaignId']) routeCampaignId = +params['campaignId'];
    });

    const obsLists = this.bleListsService.getListTypes(
      ARBleListType,
      ARBleListType.BLE_AZOTE_PRODUCT_TYPES,
      ARBleListType.BLE_AZOTE_FREQUENCIES,
    );
    const obsLists2 = this.commonListsService.getListTypes(ARCommonListType, ARCommonListType.CONSOLE_MODULATION_TYPES);
    const obsCamps = this.bleService.getCampaigns().pipe(share());
    const obsOrgs = this.getOrganisations();
    const obsSpecies = this.getBleSpecies();

    (forkJoin([obsCamps as any, obsLists as any, obsLists2 as any]) as any).subscribe(
      ([campaigns, bleLists, commonLists]: [
        ARBleCampaign[],
        Map<ARBleListType, ListItem[]>,
        Map<ARCommonListType, ListItem[]>,
      ]) => {
        this.handleCampaigns(campaigns);
        this.handleLists(bleLists, commonLists);
      },
    );

    forkJoin([obsCamps as any, obsOrgs as any, obsSpecies as any]).subscribe(([campaignsAll]: [ARBleCampaign[]]) => {
      const campaigns = (campaignsAll || []).sort(Campaign.compare).reverse();
      const hasRouteParams = routeCampaignId || routeStatus === 'incomplete' || onlyIncomplete;

      if (!ARBleParcelComponent.filterToApply || hasRouteParams) {
        // Initialize default campaign filter if no filter exists
        if (!ARBleParcelComponent.filterToApply) {
          this.campaignFilter = campaigns[0] ? campaigns[0].id : null;
        }

        // Apply route parameters (overrides previous filters)
        if (routeCampaignId) {
          this.campaignFilter = routeCampaignId;
        }

        if (routeStatus === 'incomplete' || onlyIncomplete) {
          this.completionStatusFilter = false;
        }

        // Build filter with all parameters applied (always rebuild)
        ARBleParcelComponent.filterToApply = this.buildFilter();
      }

      this.updateUIWithFilters();
      this.onChangeTable();
    });
  }

  updateUIWithFilters() {
    const f = ARBleParcelComponent.filterToApply;
    if (f) {
      if (this.isAdmin || this.isNegoce || this.isManager) {
        this.advisorFilter = f.advisorId;
        if (this.termAdvisorFilter && this.termAdvisorFilter.nativeElement) {
          this.termAdvisorFilter.nativeElement.value = this.advisorFilter || '';
        }
      }
      if (this.isAdmin) {
        this.prefixFilterIdParcel = f.prefixIdParcel;
        if (this.termPrefixFilterIdParcel && this.termPrefixFilterIdParcel.nativeElement) {
          this.termPrefixFilterIdParcel.nativeElement.value = this.prefixFilterIdParcel || '';
        }
      }
      this.prefixFilterParcel = f.prefixParcel;
      if (this.termPrefixFilterParcel && this.termPrefixFilterParcel.nativeElement) {
        this.termPrefixFilterParcel.nativeElement.value = this.prefixFilterParcel || '';
      }
      this.prefixFilterExploitation = f.prefixExploitation;
      if (this.termPrefixFilterExploitation && this.termPrefixFilterExploitation.nativeElement) {
        this.termPrefixFilterExploitation.nativeElement.value = this.prefixFilterExploitation || '';
      }
      this.termPrefixFilterExploitation.nativeElement.value = this.prefixFilterExploitation;
      this.organisationFilter = toNumber(f.organisationId);
      if (this.showOrganisationFilter && this.organisations) {
        const selectedOrg = this.organisations.find((org) => org.id === this.organisationFilter);
        if (selectedOrg) {
          this.organisationFilterText = selectedOrg.name;
        } else {
          this.organisationFilterText = '';
        }
      }
      this.campaignFilter = toNumber(f.campaignId);
      if (this.termCampaignFilter && this.termCampaignFilter.nativeElement) {
        this.termCampaignFilter.nativeElement.value = this.campaignFilter;
      }
      this.completionStatusFilter = toBoolean(f.completionStatus);
      if (this.termCompletionStatusFilter && this.termCompletionStatusFilter.nativeElement) {
        this.termCompletionStatusFilter.nativeElement.value = this.completionStatusFilter;
      }
      this.orderStatusFilter = toBoolean(f.orderStatus);
      if (this.termOrderStatusFilter && this.termOrderStatusFilter.nativeElement) {
        this.termOrderStatusFilter.nativeElement.value = this.orderStatusFilter;
      }
      this.specyFilter = toNumber(f.specy);
      if (this.termSpecyFilter && this.termSpecyFilter.nativeElement) {
        this.termSpecyFilter.nativeElement.value = this.specyFilter;
      }
    }

    if (ARBleParcelComponent.allFiltersToShow) {
      this.toggleAllFilters();
    }
  }

  ngAfterViewInit() {
    // TODO améliorer => pas de debounce quand clearFilter a été appelé
    const observables = new Array<Observable<{}>>();
    const obs1 = fromEvent(this.termPrefixFilterParcel.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs2 = fromEvent(this.termPrefixFilterExploitation.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs3 = fromEvent(this.termOrderStatusFilter.nativeElement, 'change');
    const obs4 = fromEvent(this.termCompletionStatusFilter.nativeElement, 'change');
    const obs5 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
    const obs8 = fromEvent(this.termSpecyFilter.nativeElement, 'change');
    observables.push(obs1, obs2, obs3, obs4, obs5, obs8);

    if (this.showOrganisationFilter) {
      const obs6 = fromEvent(this.termOrganisationFilterText.nativeElement, 'input').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs6);
      obs6.subscribe((term) => this.onOrgaInputChange(term));
    }

    if (this.isAdmin || this.isNegoce || this.isManager) {
      const obs9 = fromEvent(this.termAdvisorFilter.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs9);
      obs9.subscribe((term) => (this.advisorFilter = <string>this.termAdvisorFilter.nativeElement.value));
    }

    if (this.isAdmin) {
      const obs7 = fromEvent(this.termPrefixFilterIdParcel.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs7);
      obs7.subscribe((term) => (this.prefixFilterIdParcel = <number>this.termPrefixFilterIdParcel.nativeElement.value));
    }

    obs1.subscribe((term) => (this.prefixFilterParcel = <string>this.termPrefixFilterParcel.nativeElement.value));
    obs2.subscribe(
      (term) => (this.prefixFilterExploitation = <string>this.termPrefixFilterExploitation.nativeElement.value),
    );
    merge(...observables)
      .pipe(debounceTime(10))
      .subscribe((term: any) => {
        this.updateFilters();
      });
  }

  getOrganisations(): Observable<Organisation[]> {
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const isSector = KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');

    if (isAdmin || isNegoce || isSector) {
      this.showOrganisationFilter = true;
    }

    // TODO nettoyer : ne pas mettre le traitement BeApi dans getOrganisations
    // roles WANAKA
    if (isAdmin || isSector) {
      // pour Wanaka : on affiche la colonne BeApi toujours
      this.showBeApi = true;
    }

    // FIXME quand i18n est mergé : récupérer l'organisation de l'utilisateur depuis la structure i18n et décider si on affiche la colonne BeApi selon
    // pour l'instant on récupére l'organisation de l'utilisateur depuis le service
    const obs = this.organisationService.getOrganisations().pipe(share());
    obs.subscribe((orgs: any) => {
      this.organisations = orgs.sort((a: any, b: any) => a.name.localeCompare(b.name));
      this.filteredOrganisations = this.organisations;
      if (!(isAdmin || isSector)) {
        // utilisateur normal : 1 seule organisation visible
        // TODO negoce peut en voir plusieurs ? mais devrait etre coherent pour toutes ses organisations
        const organisation = this.organisations[0];
        this.showBeApi = organisation.hasBeApi;
      }
    });

    return obs;
  }

  clearFilters() {
    // seul à être non null : meme valeur que celle par défaut
    this.campaignFilter = currentCampaign(this.campaigns);

    this.prefixFilterParcel = null;
    this.prefixFilterExploitation = null;
    this.completionStatusFilter = null;
    this.prefixFilterIdParcel = null;
    this.orderStatusFilter = null;
    this.advisorFilter = null;
    this.organisationFilter = null;
    this.specyFilter = null;

    this.termPrefixFilterParcel.nativeElement.value = null;
    this.termPrefixFilterExploitation.nativeElement.value = null;
    this.termCompletionStatusFilter.nativeElement.value = null;
    this.termOrderStatusFilter.nativeElement.value = null;
    this.termSpecyFilter.nativeElement.value = null;

    if (this.termAdvisorFilter) {
      this.termAdvisorFilter.nativeElement.value = null;
    }
    if (this.termPrefixFilterIdParcel) {
      this.termPrefixFilterIdParcel.nativeElement.value = null;
    }
    if (this.showOrganisationFilter) {
      this.termOrganisationFilterText.nativeElement.value = null;
    }
    this.updateFilters();
  }

  updateFilters() {
    ARBleParcelComponent.filterToApply = this.buildFilter();

    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
    // On vide aussi les parcelles sélectionnées
    this.clearCheckboxes();
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    this.getParcelsPaginated(pageNumber, null, null, ARBleParcelComponent.filterToApply);
    //this.getNumAvailableParcels();
  }

  onChangeItemsPerPage(newValue: number) {
    const page = this.page;
    this.cdr.detectChanges();
    if (page === this.page) {
      this.onChangeTable();
    }
  }

  buildFilter(): ARBleParcelFilter {
    // binding Angular2 écrit en chaine... On fait la conversion en boolean
    const compFilter: string = '' + this.completionStatusFilter;
    const stFilter: string = '' + this.orderStatusFilter;
    const filter = {
      prefixParcel: toNull(this.prefixFilterParcel),
      prefixExploitation: toNull(this.prefixFilterExploitation),
      farmId: <any>null,
      campaignId: toNull(this.campaignFilter),
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      organisationId: toNull(this.organisationFilter),
      completionStatus: compFilter === 'true' ? true : compFilter === 'false' ? false : null,
      orderStatus: stFilter === 'true' ? true : stFilter === 'false' ? false : null,
      specy: toNull(this.specyFilter),
      advisorId: toNull(this.advisorFilter),
    };
    return filter;
  }

  addOrderItem(parcel: ARBleParcel) {
    this.router.navigate(['/orders', 'azote', 'ble', 'parcels', 'new', parcel.id]);
  }

  deleteOrderItem(orderItem: ARBleOrderItem, event: any) {
    event.stopPropagation();
    // FIXME traiter les erreurs
    this.bleService.deleteOrderItem(orderItem).subscribe(
      (res) => {
        this.onChangeTable();
      },
      (error) => this.handleError(error, this.i18nTranslator.translate('ar.ble.error.suppr')),
    );
  }

  editOrderItem(orderItem: ARBleOrderItem, event: any) {
    this.router.navigate(['/orders', 'azote', 'ble', 'parcels', 'edit', orderItem.id]);
  }

  handleError(error: any, msg: string) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.table.error.unauthorized'));
    } else {
      console.error(error);
      showErrorMessage(this.modal, msg);
    }
  }

  viewParcel(parcelId: number, event: any) {
    const context = new ParcelDetailsDialogContext();
    context.showClose = true;
    context.isBlocking = false;
    context.size = 'lg';
    context.parcelId = parcelId;
    context.showEdit = true;
    this.modal.open(ParcelDetailsDialogComponent, context);
  }

  isCampaignCurrentlyOpen(campaign: ARBleCampaign): boolean {
    return ARBleUtils.isCampaignCurrentlyOpen(campaign);
  }

  addOrder() {
    if (this.checkedParcelIds.size === 0) {
      this.router.navigate(['/orders/azote/ble/orders/new']);
    } else {
      const parcelIds = Array.from(this.checkedParcelIds.values()).join('-');
      this.router.navigate(['/orders/azote/ble/orders/new/' + parcelIds]);
    }
  }

  batchEdit() {
    if (this.checkedParcelIds.size > 1) {
      const parcelIds = Array.from(this.checkedParcelIds.values()).join('-');
      this.router.navigate(['/orders/azote/ble/parcels/batch/' + parcelIds]);
    } else if (this.checkedParcelIds.size === 1) {
      // on redirige vers la page non batch (sans les checkbox par donnée)
      const parcelId = Array.from(this.checkedParcelIds.values())[0];
      this.router.navigate(['/orders/azote/ble/parcels/edit/' + parcelId]);
    }
  }

  removeOrders() {
    const selectedParcels = this.parcels.filter((parcel) => this.checkedParcelIds.has(parcel.id));

    if (!selectedParcels.length) {
      return;
    }

    const parcelsWithOrders = selectedParcels.filter((parcel) => !!parcel.orderItem);
    const parcelsWithoutOrders = selectedParcels.filter((parcel) => !parcel.orderItem).map((parcel) => parcel.id);
    const parcelIdsToDelete = parcelsWithOrders.map((parcel) => parcel.id);

    if (parcelIdsToDelete.length === 0) {
      const summary = this.createSummaryForParcelsWithoutOrders(parcelsWithoutOrders);
      if (summary) {
        this.showDeletionSummary(summary);
      }
      return;
    }

    this.bleService.deleteParcelOrderItems(parcelIdsToDelete).subscribe(
      (summary: OrderItemDeletionSummary) => {
        this.clearCheckboxes();
        this.onChangeTable();
        const finalSummary = this.appendParcelsWithoutOrdersToSummary(summary, parcelsWithoutOrders);
        this.showDeletionSummary(finalSummary);
      },
      (error) => {
        const fallbackMessage = this.i18nTranslator.translate('ar.ble.error.suppr');
        const errorMessage = extractErrorMessage(error, fallbackMessage);
        showErrorMessage(this.modal, errorMessage);
        this.onChangeTable();
      },
    );
  }

  private createSummaryForParcelsWithoutOrders(parcelIds: number[]): OrderItemDeletionSummary {
    const failure = this.buildParcelWithoutOrderFailure(parcelIds);
    if (!failure) {
      return null;
    }

    const failureCount = failure.count || parcelIds.length;

    return {
      failures: [failure],
      failureCount,
      requestedCount: parcelIds.length,
      deletedCount: 0,
      errorKey: 'order.delete.error.summary',
      errorArgs: { count: failureCount },
    };
  }

  private appendParcelsWithoutOrdersToSummary(
    summary: OrderItemDeletionSummary,
    parcelIds: number[],
  ): OrderItemDeletionSummary {
    if (!parcelIds || !parcelIds.length) {
      return summary;
    }

    const failure = this.buildParcelWithoutOrderFailure(parcelIds);
    if (!failure) {
      return summary;
    }

    let baseSummary: OrderItemDeletionSummary;
    if (summary) {
      baseSummary = {
        failures: summary.failures ? summary.failures.slice() : [],
        requestedCount: typeof summary.requestedCount === 'number' ? summary.requestedCount : 0,
        deletedCount: typeof summary.deletedCount === 'number' ? summary.deletedCount : 0,
        failureCount: typeof summary.failureCount === 'number' ? summary.failureCount : 0,
        errorArgs: summary.errorArgs ? Object.assign({}, summary.errorArgs) : {},
      };
      if (summary.errorKey) {
        baseSummary.errorKey = summary.errorKey;
      }
      if (summary.error) {
        baseSummary.error = summary.error;
      }
    } else {
      baseSummary = {
        failures: [],
        requestedCount: 0,
        deletedCount: 0,
        failureCount: 0,
        errorArgs: {},
      };
    }

    baseSummary.failures.push(failure);
    const failureIncrement = failure.count || parcelIds.length;
    const existingFailureCount = typeof baseSummary.failureCount === 'number' ? baseSummary.failureCount : 0;
    baseSummary.failureCount = existingFailureCount + failureIncrement;
    const existingRequestedCount = typeof baseSummary.requestedCount === 'number' ? baseSummary.requestedCount : 0;
    baseSummary.requestedCount = existingRequestedCount + parcelIds.length;

    if (!baseSummary.errorKey || baseSummary.errorKey === 'order.delete.error.summary') {
      baseSummary.errorKey = 'order.delete.error.summary';
      baseSummary.errorArgs = { count: baseSummary.failureCount };
    }

    return baseSummary;
  }

  private buildParcelWithoutOrderFailure(parcelIds: number[]): OrderItemDeletionFailure {
    if (!parcelIds || !parcelIds.length) {
      return null;
    }

    return {
      messageKey: 'order.delete.error.noOrderItem',
      count: parcelIds.length,
      parcelIds: parcelIds.slice(),
    };
  }

  private showDeletionSummary(summary: OrderItemDeletionSummary) {
    if (!summary) {
      return;
    }

    const messages: { text: string; isError: boolean }[] = [];
    const deletedCount = typeof summary.deletedCount === 'number' ? summary.deletedCount : 0;
    const failureCount = typeof summary.failureCount === 'number' ? summary.failureCount : 0;
    const hasFailures = failureCount > 0 || (summary.failures && summary.failures.length > 0);

    if (deletedCount > 0) {
      const successText = this.translateDeletionMessage('order.delete.success.summary', { count: deletedCount }, null);
      if (successText) {
        messages.push({ text: successText, isError: false });
      }
    }

    if (hasFailures) {
      const summaryText = this.translateDeletionMessage(summary.errorKey, summary.errorArgs, summary.error);
      if (summaryText) {
        messages.push({ text: summaryText, isError: true });
      }

      if (summary.failures && summary.failures.length) {
        for (const failure of summary.failures) {
          const failureText = this.translateDeletionMessage(
            failure.messageKey,
            failure.messageArgs,
            failure.defaultMessage,
          );
          let messageLine = failureText || '';
          const detailParts: string[] = [];
          if (failure.count !== undefined && failure.count !== null) {
            const parcelsLabel = this.i18nTranslator.translate('planning.error.parcels');
            detailParts.push(`${failure.count} ${parcelsLabel}`);
          }

          if (detailParts.length) {
            messageLine += ` (${detailParts.join(' - ')})`;
          }

          if (messageLine) {
            messages.push({ text: messageLine, isError: true });
          }
        }
      }
    }

    if (!messages.length) {
      return;
    }

    const title = this.i18nTranslator.translate('order.delete.error.dialogTitle');
    this.openDeletionModal(title, messages);
  }

  private translateDeletionMessage(key: string, args: any, fallback: string) {
    if (key) {
      const translation = this.i18nTranslator.translate(key, args);
      if (translation && translation !== key) {
        return translation;
      }
    }

    if (fallback) {
      return fallback;
    }

    return key || '';
  }

  private openDeletionModal(title: string, messages: { text: string; isError: boolean }[]) {
    const context = new StatusModalContext();
    Object.assign(context, {
      title,
      messages,
      dialogClass: 'modal-dialog',
      size: 'lg',
      showClose: true,
      keyboard: true,
      blocking: false,
      closeByClickingOutside: false,
    });

    this.modal.open(CustomStatusModalComponent, context).then((dialogRef) => {
      dialogRef.result.catch(() => {
        console.log('User closed status modal');
      });
    });
  }

  addParcel() {
    this.router.navigate(['/parcels', 'new']);
  }

  handleCampaigns(campaigns: ARBleCampaign[]) {
    this.campaigns = campaigns.sort(Campaign.compare).reverse();
    campaigns = campaigns.filter((x) => ARBleUtils.isCampaignCurrentlyOpen(x));
    for (const campaign of campaigns) {
      const dateChecks = {
        campaign: campaign,
        isT1OrderCurrentlyOpen: ARBleUtils.isT1OrderCurrentlyOpen(campaign),
        isT2OrderCurrentlyOpen: ARBleUtils.isT2OrderCurrentlyOpen(campaign),
        isT3OrderCurrentlyOpen: ARBleUtils.isT3OrderCurrentlyOpen(campaign),
        isT1DataCurrentlyOpen: ARBleUtils.isT1DataCurrentlyOpen(campaign),
        isT2DataCurrentlyOpen: ARBleUtils.isT2DataCurrentlyOpen(campaign),
        isT3DataHasStarted: ARBleUtils.isT3DataHasStarted(campaign),
        isT3DataCurrentlyOpen: ARBleUtils.isT3DataCurrentlyOpen(campaign),
      };
      this.openedCampaignsDateChecks.set(campaign.id, dateChecks);
    }
  }

  isParcelChecked(parcel: ARBleParcel): boolean {
    return this.checkedParcelIds.has(parcel.id);
  }

  isParcelDisabled(parcel: ARBleParcel): boolean {
    const c = this.openedCampaignsDateChecks.get(parcel.campaign.id);
    if (c) {
      // disabled si : n'a pas encore d'OrderItem, a déjà une commande ou n'est pas commandable
      return (
        !parcel.orderItem ||
        !ARBleUtils.canBeOrdered(
          parcel.orderItem,
          !!c.campaign,
          c.isT1OrderCurrentlyOpen,
          c.isT2OrderCurrentlyOpen,
          c.isT3OrderCurrentlyOpen,
          c.isT3DataHasStarted,
          c.isT3DataCurrentlyOpen,
          this.productTypesIndex,
          this.frequenciesIndex,
          this.consoleModulationTypesIndex,
        )
      );
    } else {
      return true;
    }
  }

  handleLists(bleLists: Map<ARBleListType, ListItem[]>, commonLists: Map<ARCommonListType, ListItem[]>) {
    const azoteProductTypes = bleLists.get(ARBleListType.BLE_AZOTE_PRODUCT_TYPES);
    for (const p of azoteProductTypes) {
      this.productTypesIndex.set(p.id, p.code);
    }
    const pastAzoteFrequencies = bleLists.get(ARBleListType.BLE_AZOTE_FREQUENCIES);
    for (const p of pastAzoteFrequencies) {
      this.frequenciesIndex.set(p.id, p.code);
    }
    const consoleModulationTypes = commonLists.get(ARCommonListType.CONSOLE_MODULATION_TYPES);
    for (const p of consoleModulationTypes) {
      this.consoleModulationTypesIndex.set(p.id, p.code);
    }
  }

  onCheckboxClicked(index: number) {
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    const parcel = this.parcels[index];
    const checkbox = checkboxes[index];

    if (checkbox.checked) {
      this.checkedParcelIds.add(parcel.id);
    } else {
      this.checkedParcelIds.delete(parcel.id);
    }
  }

  onCheckboxAllClicked() {
    if (this.checkboxAll.nativeElement.checked) {
      const filter = this.buildFilter();
      this.bleService.getAllParcelIds(filter).subscribe(
        (x) => {
          this.lengthAllParcels = x.length;

          for (const idx of x) {
            this.checkedParcelIds.add(idx);
          }
          // pas de modèle : on coche à la main
          const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
          for (let i = 0; i < checkboxes.length; i++) {
            const checkbox = checkboxes[i];
            checkbox.checked = true;
          }
        },
        (error) => {
          this.handleError(error, this.i18nTranslator.translate('ar.table.error.selection'));
        },
      );
    } else {
      this.clearCheckboxes();
    }
  }

  clearCheckboxes() {
    this.lengthAllParcels = undefined;
    this.checkboxAll.nativeElement.checked = false;
    // decoche tout
    this.checkedParcelIds.clear();
    // pas de modèle : on décoche à la main
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    for (const checkbox of checkboxes) {
      checkbox.checked = false;
    }
  }

  isCheckboxAllChecked(): boolean {
    return this.lengthAllParcels === this.checkedParcelIds.size;
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      this.allFilters.nativeElement.style.display = 'none';
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      this.allFilters.nativeElement.style.display = 'block';
    }

    ARBleParcelComponent.allFiltersToShow = this.showAllFilters;
  }
  getBleSpecies(): Observable<Map<ListType, ListItem[]>> {
    const obs = this.listsService.getListTypes(ListType, ListType.SPECIES).pipe(share());
    obs.subscribe(
      (lists: Map<ListType, ListItem[]>) => {
        const species = lists.get(ListType.SPECIES);
        const correctSpecy: any[] = [];
        this.speciesDict = new Map<number, Specy>();
        for (const specy of species) {
          if (specy.code.includes('BLE')) {
            correctSpecy.push(specy);
            this.speciesDict.set(specy.id, specy);
          }
        }
        this.species = correctSpecy.sort((a, b) => a.label.localeCompare(b.label));
      },
      (err) => this.handleError(err, this.i18nTranslator.translate('ar.ble.error.cultures')),
    );
    return obs;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onOrgaInputChange(event: any) {
    let raw = '';
    if (event && event.target && typeof event.target.value === 'string') {
      raw = event.target.value;
    } else if (event && event.currentTarget && typeof event.currentTarget.value === 'string') {
      raw = event.currentTarget.value;
    }

    const searchText = (raw || '').toLowerCase();

    if (!searchText || searchText.replace(/\s+/g, '') === '') {
      this.filteredOrganisations = this.organisations || [];
      if (this.organisationFilter !== null) {
        this.organisationFilter = null;
        this.updateFilters();
      }
    } else {
      const list = this.organisations || [];
      this.filteredOrganisations = list.filter(function (org: any) {
        const name = org && org.name ? ('' + org.name).toLowerCase() : '';
        return name.indexOf(searchText) !== -1;
      });
    }
  }

  selectOrganisation(id: number, name: string) {
    this.organisationFilter = id;
    this.organisationFilterText = name || '';
    this.showOrgaDropdown = false;
    this.updateFilters();
  }

  hideOrgaDropdown() {
    setTimeout(() => (this.showOrgaDropdown = false), 200);
  }
}
